import { apiFetch, loadSession } from "./client";
import type { ReceivedUnit } from "./commerce";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5199";

export type AttributeOption = { value: string; label: string };

/**
 * One of the tenant's own product fields.
 *
 * The form is built from these. Nothing about "Cloth material" or "Art work" is written into the
 * client — it renders whatever the tenant has defined (ADR-0009).
 */
export type AttributeDefinition = {
  id: string;
  key: string;
  name: string;
  hint: string | null;
  dataType: "Text" | "LongText" | "Number" | "Boolean" | "Date" | "Select" | "MultiSelect";
  categoryId: string | null;
  isRequired: boolean;
  sortOrder: number;
  isActive: boolean;
  /** Whether the owner allows this field to split a product into several SKUs. Select fields only. */
  isVariantAxis: boolean;
  options: AttributeOption[];
};

/** Which values are ticked on one axis. Sent to the API, which works out the combinations. */
export type VariantAxisSelection = { attributeDefinitionId: string; values: string[] };

/** One SKU a set of choices would produce. Every field here is composed by the API. */
export type VariantPreviewRow = { optionValues: string[]; label: string; skuSuffix: string };

export type VariantPreview = { optionNames: string[]; combinations: VariantPreviewRow[] };

export type VariantQuantity = {
  skuSuffix: string;
  quantity: number;
  purchasePrice: number | null;
  sellingPrice: number | null;
};

export type AttributeValueInput = {
  attributeDefinitionId: string;
  value: string | null;
  selectedValues: string[] | null;
};

export type QuickAddVariantResult = {
  variantId: string;
  sku: string;
  optionSummary: string;
  quantity: number;
};

export type QuickAddResult = {
  productId: string;
  productCode: string;
  variantId: string;
  sku: string;
  receiptNumber: string | null;
  units: ReceivedUnit[];
  variants: QuickAddVariantResult[];
};

export type ProductAttributeValue = {
  attributeDefinitionId: string;
  value: string | null;
  selectedValues: string[];
};

export type EditableVariant = {
  id: string;
  sku: string;
  optionSummary: string;
  sellingPrice: number | null;
  costPrice: number | null;
  onHand: number;
  isActive: boolean;
};

export type SaveVariant = {
  id: string;
  sellingPrice: number | null;
  isActive: boolean;
  mrp: number | null;
  barcode: string | null;
};

export type ProductPhoto = {
  id: string;
  url: string;
  isPrimary: boolean;
  sortOrder: number;
  byteSize: number;
  contentType: string;
  altText: string | null;
};

export type ProductStockLine = {
  productVariantId: string;
  sku: string;
  siteId: string;
  siteCode: string;
  siteName: string;
  available: number;
  onSiteTotal: number;
};

export type ProductDetail = {
  id: string;
  productCode: string;
  name: string;
  description: string | null;
  categoryId: string;
  categoryPath: string[];
  optionNames: string[];
  attributes: ProductAttributeValue[];
  variants: EditableVariant[];
  primaryPhotoUrl: string | null;
  brand: string | null;
  shortDescription: string | null;
  isActive: boolean;
  photos: ProductPhoto[];
  stock: ProductStockLine[];
};

export type RemoveProductResult = {
  removed: boolean;
  quantityWrittenOff: number;
  serialsWrittenOff: string[];
  explanation: string;
};

/** How stock on hand is ordered. Newest is the default: after a stock-in, that is what you want. */
export type StockSort = "newest" | "oldest" | "quantity" | "value" | "name";

export type SerialToken = { token: string; name: string; example: string };

export type SerialFormat = {
  pattern: string;
  prefix: string | null;
  appendCheckCharacter: boolean;
  checkSeparator: string;
  preview: string;
  scanPreview: string;
  tokens: SerialToken[];
  nextSequence: number;
};

export type SerialPreview = {
  preview: string;
  scanPreview: string;
  barcodeWidthMm: number;
  fitsLabel: boolean;
  warning: string | null;
};

export type SaveSerialFormat = {
  pattern: string;
  prefix: string | null;
  appendCheckCharacter: boolean;
  checkSeparator: string;
};

export type StockRow = {
  productVariantId: string;
  sku: string;
  productName: string;
  productId: string;
  primaryPhotoUrl: string | null;
  optionSummary: string;
  categoryId: string;
  categoryPath: string[];
  siteId: string;
  siteCode: string;
  siteName: string;
  available: number;
  reserved: number;
  damaged: number;
  quarantine: number;
  inTransit: number;
  onSiteTotal: number;
  averageCost: number | null;
  stockValue: number | null;
};

/** A subtotal the API rolled up — one category (with everything under it), or one store. */
export type StockGroup = {
  id: string;
  label: string;
  detail: string | null;
  depth: number;
  units: number;
  value: number;
  distinctSkus: number;
};

export type StockOnHand = {
  rows: StockRow[];
  totalUnits: number;
  totalValue: number;
  distinctSkus: number;
  byCategory: StockGroup[];
  bySite: StockGroup[];
};

export const catalogue = {
  attributes: (categoryId?: string) =>
    apiFetch<AttributeDefinition[]>(
      `/settings/attributes${categoryId ? `?categoryId=${categoryId}` : ""}`,
    ),

  saveAttribute: (definition: {
    key: string;
    name: string;
    dataType: AttributeDefinition["dataType"];
    options: AttributeOption[] | null;
    categoryId: string | null;
    hint: string | null;
    isRequired: boolean;
    sortOrder: number;
    isVariantAxis: boolean;
  }) => apiFetch<AttributeDefinition>("/settings/attributes", { method: "POST", body: definition }),

  deactivateAttribute: (id: string) =>
    apiFetch<AttributeDefinition>(`/settings/attributes/${id}/deactivate`, { method: "POST" }),

  reactivateAttribute: (id: string) =>
    apiFetch<AttributeDefinition>(`/settings/attributes/${id}/reactivate`, { method: "POST" }),

  quickAdd: (body: {
    name: string;
    description: string | null;
    categoryId: string;
    purchasePrice: number;
    sellingPrice: number | null;
    quantity: number;
    siteId: string | null;
    storageLocationId: string | null;
    attributes: AttributeValueInput[];
    sku: string | null;
    axes: VariantAxisSelection[] | null;
    variantQuantities: VariantQuantity[] | null;
  }) => apiFetch<QuickAddResult>("/products/quick-add", { method: "POST", body }),

  /**
   * Asks what SKUs a set of choices would produce.
   *
   * The combinations and their SKU fragments come from the API, never from here: two clients
   * composing them slightly differently would produce two SKUs for the same garment (ADR-0009).
   */
  variantPreview: (categoryId: string, axes: VariantAxisSelection[]) =>
    apiFetch<VariantPreview>("/products/variant-preview", {
      method: "POST",
      body: { categoryId, axes },
    }),

  product: (id: string) => apiFetch<ProductDetail>(`/products/${id}`),

  updateProduct: (
    id: string,
    body: {
      name: string;
      description: string | null;
      shortDescription: string | null;
      brand: string | null;
      categoryId: string;
      attributes: AttributeValueInput[];
      variants: SaveVariant[];
    },
  ) => apiFetch<ProductDetail>(`/products/${id}`, { method: "PUT", body }),

  /**
   * Removes a piece.
   *
   * `expectedQuantity` is what the screen showed. The API refuses if the real figure has moved,
   * so a dialog left open while someone else received stock cannot write off more than the person
   * agreed to.
   */
  removeProduct: (id: string, expectedQuantity: number, reason?: string) => {
    const query = new URLSearchParams({ expectedQuantity: String(expectedQuantity) });
    if (reason) query.set("reason", reason);

    return apiFetch<RemoveProductResult>(`/products/${id}?${query}`, { method: "DELETE" });
  },

  photos: (productId: string) => apiFetch<ProductPhoto[]>(`/products/${productId}/photos`),

  makePhotoPrimary: (productId: string, photoId: string) =>
    apiFetch<ProductPhoto[]>(`/products/${productId}/photos/${photoId}/primary`, {
      method: "POST",
    }),

  deletePhoto: (productId: string, photoId: string) =>
    apiFetch<ProductPhoto[]>(`/products/${productId}/photos/${photoId}`, { method: "DELETE" }),

  reorderPhotos: (productId: string, photoIds: string[]) =>
    apiFetch<ProductPhoto[]>(`/products/${productId}/photos/order`, {
      method: "PUT",
      body: { photoIds },
    }),

  serialFormat: () => apiFetch<SerialFormat>("/settings/serial-format"),

  previewSerialFormat: (body: SaveSerialFormat) =>
    apiFetch<SerialPreview>("/settings/serial-format/preview", { method: "POST", body }),

  saveSerialFormat: (body: SaveSerialFormat) =>
    apiFetch<SerialFormat>("/settings/serial-format", { method: "PUT", body }),

  /**
   * Uploads one or more photos.
   *
   * Not through apiFetch: that sets a JSON content type, and multipart needs the browser to set
   * its own boundary. Sent as multipart rather than base64 because a phone photo is several
   * megabytes and base64 would inflate it by a third for nothing.
   *
   * All the files go in one request. Four requests from a phone on shop wifi fail partway often
   * enough to matter, and a partial upload reporting success would leave someone believing a
   * picture was saved.
   */
  async uploadPhotos(productId: string, files: File[]): Promise<ProductPhoto[]> {
    const session = loadSession();
    const form = new FormData();
    for (const file of files) {
      form.append("files", file);
    }

    const response = await fetch(`${API_BASE}/api/v1/products/${productId}/photos`, {
      method: "POST",
      headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
      body: form,
    });

    if (!response.ok) {
      const problem = await response.json().catch(() => ({}));
      throw new Error(problem.detail ?? problem.title ?? "Could not upload those photos.");
    }

    return response.json();
  },

  /** Builds a full URL for a photo path returned by the API. */
  photoUrl: (path: string) => `${API_BASE}${path}`,

  stockOnHand: (
    params: {
      siteId?: string;
      categoryId?: string;
      search?: string;
      inStockOnly?: boolean;
      sort?: StockSort;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.siteId) query.set("siteId", params.siteId);
    if (params.categoryId) query.set("categoryId", params.categoryId);
    if (params.search) query.set("search", params.search);
    if (params.sort) query.set("sort", params.sort);
    if (params.inStockOnly === false) query.set("inStockOnly", "false");

    const suffix = query.toString();
    return apiFetch<StockOnHand>(`/stock/on-hand${suffix ? `?${suffix}` : ""}`);
  },
};

/**
 * Fetches an image the API protects with a bearer token, as an object URL.
 *
 * An <img src> cannot send an Authorization header, and product photos are tenant data served
 * through the API so the permission check happens — so they have to be fetched and wrapped.
 */
export async function fetchProtectedImage(path: string): Promise<string | null> {
  const session = loadSession();

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
    });

    if (!response.ok) return null;

    return URL.createObjectURL(await response.blob());
  } catch {
    return null;
  }
}
